import React, { createContext, useContext, useState, useEffect } from 'react';

const UnreadContext = createContext();

export const useUnread = () => {
  const context = useContext(UnreadContext);
  if (!context) {
    throw new Error('useUnread must be used within an UnreadProvider');
  }
  return context;
};

export const UnreadProvider = ({ children }) => {
  const [unreadCounts, setUnreadCounts] = useState({
    messages: 0,
    events: 0,
    notifications: 0
  });

  const [unreadItems, setUnreadItems] = useState({
    messages: [],
    events: [],
    notifications: []
  });

  // Mark items as read
  const markAsRead = (type, itemId = null) => {
    if (itemId) {
      // Mark specific item as read
      setUnreadItems(prev => ({
        ...prev,
        [type]: prev[type].filter(item => item.id !== itemId)
      }));
    } else {
      // Mark all items of a type as read
      setUnreadItems(prev => ({
        ...prev,
        [type]: []
      }));
    }
    
    // Update counts
    updateCounts();
  };

  // Add unread item
  const addUnread = (type, item) => {
    setUnreadItems(prev => ({
      ...prev,
      [type]: [...prev[type], { ...item, timestamp: new Date() }]
    }));
    updateCounts();
  };

  // Update counts based on unread items
  const updateCounts = () => {
    setUnreadCounts(prev => ({
      ...prev,
      messages: unreadItems.messages.length,
      events: unreadItems.events.length,
      notifications: unreadItems.notifications.length
    }));
  };

  // Get total unread count
  const getTotalUnread = () => {
    return Object.values(unreadCounts).reduce((total, count) => total + count, 0);
  };

  // Initialize with some demo unread items
  useEffect(() => {
    const demoUnreadItems = {
      messages: [
        { id: 1, title: 'New message in Team Chat', type: 'message' },
        { id: 2, title: 'Direct message from Coach Mike', type: 'direct_message' }
      ],
      events: [
        { id: 1, title: 'Practice reminder', type: 'event_reminder' }
      ],
      notifications: [
        { id: 1, title: 'Team member joined', type: 'member_joined' }
      ]
    };
    
    setUnreadItems(demoUnreadItems);
    updateCounts();
  }, []);

  const value = {
    unreadCounts,
    unreadItems,
    markAsRead,
    addUnread,
    getTotalUnread
  };

  return (
    <UnreadContext.Provider value={value}>
      {children}
    </UnreadContext.Provider>
  );
};